import type { KeyboardEvent, RefObject } from "react";
import { useCallback, useRef } from "react";

const FOCUSABLE_ELEMENT_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "textarea:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
].join(",");

const getFocusableElements = (container: HTMLElement): HTMLElement[] =>
    Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENT_SELECTOR));

export const useOnboardingAgentFocusTrap = (): {
    readonly focusTrapRef: RefObject<HTMLDivElement | null>;
    readonly handleFocusTrapKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
} => {
    const focusTrapRef = useRef<HTMLDivElement>(null);

    const handleFocusTrapKeyDown = useCallback((event: KeyboardEvent<HTMLElement>): void => {
        if (event.key !== "Tab") {
            return;
        }

        const container = focusTrapRef.current;
        if (!container) {
            return;
        }

        const focusableElements = getFocusableElements(container);
        const firstFocusableElement = focusableElements[0];
        const lastFocusableElement = focusableElements.at(-1);

        if (!firstFocusableElement || !lastFocusableElement) {
            event.preventDefault();
            return;
        }

        if (event.shiftKey && document.activeElement === firstFocusableElement) {
            event.preventDefault();
            lastFocusableElement.focus();
            return;
        }

        if (!event.shiftKey && document.activeElement === lastFocusableElement) {
            event.preventDefault();
            firstFocusableElement.focus();
        }
    }, []);

    return { focusTrapRef, handleFocusTrapKeyDown };
};
